import { Outlet, createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

// デバイス棚卸しページ共通レイアウト
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';

import { Header } from './-components/Header';
import {
  ValidationError,
  getPackageInfo,
  validatePatternId,
  validateRequestId,
} from './-domain/selectors';
import { useHeader } from './-hooks/useHeader';

const SearchSchema = z
  .object({
    patternId: z.string(),
    requestId: z.string().optional(),
    packageInstanceId: z.string().optional(),
  })
  .refine(
    (data) =>
      data.requestId !== undefined || data.packageInstanceId !== undefined,
    {
      message: 'requestId または packageInstanceId のどちらかが必須です',
    },
  );

export const Route = createFileRoute(
  '/_staff/it-operation-core/device-inventory',
)({
  validateSearch: zodValidator(SearchSchema),
  beforeLoad: ({ search }) => {
    try {
      const validatedPatternId = validatePatternId(search.patternId);
      const validatedRequestId = search.requestId
        ? validateRequestId(search.requestId)
        : undefined;
      return { validatedPatternId, validatedRequestId };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw new Error(`入力値エラー: ${error.message}`);
      }
      throw new Error('データの取得に失敗しました。');
    }
  },
  // patternId が変わった時のみ loader を再実行（ソート/フィルター変更では再実行しない）
  loaderDeps: ({ search }) => ({ patternId: search.patternId }),
  staleTime: Infinity,
  loader: async ({ context }) => {
    try {
      const { validatedPatternId } = context;
      const packageInfo = await getPackageInfo(validatedPatternId);

      if (!packageInfo) {
        throw new ValidationError('パッケージが見つかりません', 'patternId');
      }

      return {
        packageId: packageInfo.packageId,
        packageInfo: packageInfo.packageInfo,
        patternInfo: packageInfo.patternInfo,
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw new Error(`入力値エラー: ${error.message}`);
      }
      throw new Error('データの取得に失敗しました。');
    }
  },
  pendingComponent: () => <ViewportLoader className="w-full" />,
  component: LayoutComponent,
});

function LayoutComponent() {
  const { patternId, requestId } = Route.useSearch({
    select: (s) => ({ patternId: s.patternId, requestId: s.requestId }),
  });
  const loaderData = Route.useLoaderData();
  const header = useHeader({ patternId, requestId: requestId ?? '' });

  const { packageId, packageInfo, patternInfo } = loaderData;

  // requestIdがない場合（packageInstanceIdからrequestIdを取得中）
  // Headerは表示せず、Outletのみ表示して子コンポーネントでrequestIdを取得
  if (!requestId) {
    return (
      <VStack>
        <Outlet />
      </VStack>
    );
  }

  return (
    <VStack>
      <Header
        packageId={packageId}
        packageName={packageInfo.name}
        patternName={patternInfo.name}
        patternId={patternId}
        requestId={requestId}
        aggregatedRequestedFor={[]}
        actions={header.actions}
      />
      <Outlet />
    </VStack>
  );
}
